import { useCallback, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
} from "@salestrak/ui";
import { IssueMenu } from "#app/components/issue-actions/issue-menu";
import { useActivityContextValue } from "#app/hooks/issue-view/use-activity-context-value";
import {
  CreateIssueProvider,
  useCreateIssueMode,
} from "#app/lib/create-issue-context";
import { canEdit } from "#app/lib/utils/access-level";
import { ActivityProvider } from "./activity/activity-context";
import { NoteComposer } from "./activity/note-composer";
import { IssueBody } from "./issue-body";
import { useIssueViewDialogState } from "./issue-view-dialog-state";

const VIEW_SNAP_POINTS = [0.6, 0.92];
const OPEN_SNAP_POINT = 0.92;

// The scroller is the DrawerPanel (the ONE scrollable). IssueBody owns its own
// horizontal padding, so the panel contributes only the top gap; the bottom
// safe area is DrawerFooter/DrawerPopup's job, not a measured inset.
const PANEL_INSET = "px-0 pt-3";

export function IssueViewSheet() {
  const dialog = useIssueViewDialogState();
  const [createCanAutoFocus, setCreateCanAutoFocus] = useState(false);
  const [viewHasSettled, setViewHasSettled] = useState(false);

  const viewState = dialog.state?.mode === "view" ? dialog.state : null;
  const createState = dialog.state?.mode === "create" ? dialog.state : null;

  // Gate the note auto-scroll until the view sheet finishes its open animation.
  // scrollIntoView mid-snap fights the drawer's transform, landing the scroll
  // off-target. `onOpenChangeComplete` is the settle signal.
  const handleViewOpenComplete = useCallback(
    (open: boolean) => setViewHasSettled(open),
    [],
  );

  // Gate title autofocus the same way: focusing mid-animation opens the virtual
  // keyboard, which resizes the visual viewport and desyncs the drawer's
  // transform math.
  const handleCreateOpenComplete = useCallback(
    (open: boolean) => setCreateCanAutoFocus(open),
    [],
  );

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) dialog.closeIssue();
    },
    [dialog],
  );

  // Call unconditionally (rules of hooks). Inner query skips when issueId is empty.
  const activity = useActivityContextValue(
    viewState?.issue.issueId ?? "",
    viewState?.focusNoteId,
    viewHasSettled,
  );
  const showActivityFooter = !!viewState && dialog.activeTab === "activity";

  const bodyProps = {
    activeTab: dialog.activeTab,
    setActiveTab: dialog.setActiveTab,
    propertiesColumns: 1 as const,
    contentMaxWidth: undefined,
    isMobile: true,
  };

  return (
    <>
      {viewState && (
        <Drawer
          open
          onOpenChange={onOpenChange}
          onOpenChangeComplete={handleViewOpenComplete}
          snapPoints={VIEW_SNAP_POINTS}
          defaultSnapPoint={OPEN_SNAP_POINT}
        >
          <DrawerPopup showBar>
            <DrawerHeader>
              <Box direction="row" align="center" justify="between" gap="sm">
                <DrawerTitle>{viewState.issue.name ?? "Project"}</DrawerTitle>
                <IssueMenu
                  issue={viewState.issue}
                  readOnly={!canEdit(viewState.issue.viewerAccessLevel)}
                />
              </Box>
            </DrawerHeader>
            <DrawerPanel className={PANEL_INSET}>
              <IssueBody
                {...bodyProps}
                issue={viewState.issue}
                focusNoteId={viewState.focusNoteId}
                focusCustomFieldId={viewState.focusCustomFieldId}
                activityContext={activity}
              />
            </DrawerPanel>
            {showActivityFooter ? (
              <DrawerFooter variant="bare">
                <ActivityProvider value={activity.value}>
                  <Box padding="sm" fill="width">
                    <NoteComposer issueId={viewState.issue.issueId} />
                  </Box>
                </ActivityProvider>
              </DrawerFooter>
            ) : null}
          </DrawerPopup>
        </Drawer>
      )}

      {createState && (
        <Drawer
          open
          onOpenChange={onOpenChange}
          onOpenChangeComplete={handleCreateOpenComplete}
          snapPoints={VIEW_SNAP_POINTS}
          defaultSnapPoint={OPEN_SNAP_POINT}
        >
          <DrawerPopup showBar>
            <CreateIssueProvider
              key={
                createState.address?.wholeAddress ??
                (createState.address?.latLng
                  ? `${createState.address.latLng.lat},${createState.address.latLng.lng}`
                  : "no-address")
              }
              initialWorkflowId={createState.workflowId}
              initialStageId={createState.stageId}
              initialAddress={createState.address}
              initialName={createState.name}
              source={createState.source}
              onCreated={dialog.onCreated}
              canAutoFocus={createCanAutoFocus}
            >
              <CreateSheetHeader closeIssue={dialog.closeIssue} />
              <DrawerPanel className={PANEL_INSET}>
                <IssueBody {...bodyProps} issue={null} />
              </DrawerPanel>
            </CreateIssueProvider>
          </DrawerPopup>
        </Drawer>
      )}
    </>
  );
}

/** The drawer header takes ordinary children, so Cancel/Create are real
 *  Buttons flanking the title. */
function CreateSheetHeader({ closeIssue }: { closeIssue: () => void }) {
  const { submit, isSubmitting } = useCreateIssueMode()!;
  return (
    <DrawerHeader>
      <Box direction="row" align="center" justify="between" gap="sm">
        <Button variant="ghost" size="sm" onClick={closeIssue}>
          Cancel
        </Button>
        <DrawerTitle>Create project</DrawerTitle>
        <Button
          size="sm"
          onClick={submit}
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          Create
        </Button>
      </Box>
    </DrawerHeader>
  );
}
